import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "mt-10 text-center"
}
const _hoisted_2 = { class: "mt-4 text-lg font-bold text-gray-900 dark:text-white" }
const _hoisted_3 = { class: "mt-2 text-sm text-gray-500 dark:text-gray-400" }
const _hoisted_4 = { class: "mt-10" }

import lookupInvoice, {
		guestPayErrorMessage,
		InvoiceLookupPayload
	} from '@/utilities/guestPayApi'

	// components
	import InvoiceLookupForm from './components/InvoiceLookupForm.vue'
	import PayInvoiceDialog from './components/PayInvoiceDialog.vue'

	// icons
	import {
		CheckCircleIcon
	} from '@heroicons/vue/24/outline'

	// types
	import {
		Invoice
	} from '@/types'

	// utilities
	import {
		currencyFormatter
	} from '@/utilities/format'

	import {
		applyInvoiceDueStatus,
		clearPendingVerification
	} from '@/utilities/invoiceStatus'

	// vue
	import {
		nextTick,
		ref
	} from 'vue'

	// the status FileMaker sets from the payment_intent webhook once microdeposits are on their way
	interface PaymentConfirmation {
		invoiceNumber: string
		amount: number
		settled: boolean
	}

	// the same message covers a wrong invoice number, a wrong account ID, and a mismatched pair, so
	// the page never confirms that one half of a guess was right
	
	const __default__ = { name: 'PayInvoiceMain' } // name the component

export default /*@__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

	// api
	const PENDING_VERIFICATION_STATUS = 'pending_verification'

	// what the dialog reports once a payment goes through. the amount is what Stripe actually took,
	// card fee included, and settled is false for a bank payment that is still clearing
	const NOT_FOUND_MESSAGE = "We couldn't find that invoice. Please check the account and invoice numbers and try again."
	const ERROR_MESSAGE = "There was an issue looking up your invoice. Please try again or contact us for assistance."

	const invoice = ref<Invoice | null>(null)

	// kept after a successful lookup. the payment routes have no Turnstile of their own, so this
	// signed token is what authorizes them, and it carries the invoice and account it was issued for
	const paymentToken = ref("")

	const confirmation = ref<PaymentConfirmation | null>(null)
	const loading = ref(false)
	const message = ref("")

	const turnstileRef = ref<{ execute: () => Promise<string> } | null>(null)
	const payInvoiceDialog = ref<{ openDialog: () => Promise<void> } | null>(null)

	// looks up an invoice and shows it, falling back to the form with an explanation when it misses.
	// a filled honeypot is reported as not found rather than as a rejection
	async function findInvoice(submitted: Omit<InvoiceLookupPayload, "token">) {
		try {
			loading.value = true
			message.value = ""

			if (submitted.honeypot) {
				message.value = NOT_FOUND_MESSAGE
				return
			}

			const token = await turnstileRef.value?.execute() ?? ""

			const response = await lookupInvoice({
				invoiceNumber: submitted.invoiceNumber.trim(),
				accountNumber: submitted.accountNumber.trim(),
				honeypot: submitted.honeypot,
				token,
			})

			if (response.result === 'OK' && response.invoice) {
				invoice.value = withDueDate(response.invoice)
				paymentToken.value = response.paymentToken ?? ""

				// the dialog is only rendered once there is an invoice, so it has to exist before
				// it can be told to open
				await nextTick()
				await payInvoiceDialog.value?.openDialog()
				return
			}

			// the Lambda explains its own rejections, such as a failed security check, so its message
			// is preferred over the generic one wherever it sent one
			message.value = response.message
				|| (response.result === 'notFound' ? NOT_FOUND_MESSAGE : ERROR_MESSAGE)
		}
		catch (error: any) {
			// Amplify rejects on any non 2xx, so a rejection the Lambda explained arrives as a throw
			message.value = guestPayErrorMessage(error) || ERROR_MESSAGE
		}
		finally {
			loading.value = false
		}
	}

	// swaps the form for a confirmation once a payment goes through. the invoice is not looked up
	// again: FileMaker only hears from the webhook, so a fresh read would still show it unpaid, and
	// reopening the dialog would only start a second payment for the Lambda to refuse
	function showConfirmation(paid: PaymentConfirmation) {
		confirmation.value = paid

		// drops the invoice and token too, which unmounts the dialog
		clearInvoice()
	}

	// returns from the confirmation to an empty lookup form
	function payAnotherInvoice() {
		confirmation.value = null
	}

	// fills in the calculated due date and past due status on a copy of the invoice, using the same
	// rules as the signed in invoice list: past due on or after the due date
	function withDueDate(found: Invoice): Invoice {
		const dated = { ...found }

		applyInvoiceDueStatus(dated)

		return dated
	}

	// marks the invoice as awaiting a deposit verification straight away. the mirror of
	// clearPendingVerification: FileMaker only finds out from the webhook, and the card has to show
	// what the visitor just started rather than waiting for the next lookup
	function markPendingVerification() {
		if (!invoice.value) return

		invoice.value.paymentStatus = PENDING_VERIFICATION_STATUS
	}

	// returns to the lookup form so another invoice can be found
	function clearInvoice() {
		invoice.value = null
		paymentToken.value = ""
		message.value = ""
	}

return (_ctx: any,_cache: any) => {
  const _component_tw_button = _resolveComponent("tw-button")!
  const _component_v_divider_label = _resolveComponent("v-divider-label")!
  const _component_router_link = _resolveComponent("router-link")!
  const _component_cloudflare_turnstile = _resolveComponent("cloudflare-turnstile")!

  return (_openBlock(), _createElementBlock("div", null, [
    _cache[7] || (_cache[7] = _createElementVNode("h2", { class: "mt-8 text-2xl font-bold leading-9 tracking-tight text-gray-900 dark:text-white" }, " Pay an Invoice ", -1)),
    (confirmation.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(_unref(CheckCircleIcon), {
            class: "mx-auto h-12 w-12 text-green-500",
            "aria-hidden": "true"
          }),
          _createElementVNode("h3", _hoisted_2, _toDisplayString(confirmation.value.settled ? 'Payment Received' : 'Payment Submitted'), 1),
          _createElementVNode("p", _hoisted_3, [
            (confirmation.value.settled)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createTextVNode(" Thank you. Your payment of " + _toDisplayString(_unref(currencyFormatter).format(confirmation.value.amount)) + " for invoice #" + _toDisplayString(confirmation.value.invoiceNumber) + " has been received. ", 1)
                ], 64))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _createTextVNode(" Thank you. Your payment of " + _toDisplayString(_unref(currencyFormatter).format(confirmation.value.amount)) + " for invoice #" + _toDisplayString(confirmation.value.invoiceNumber) + " is processing. ACH Bank payments can take up to 4 business days to process. ", 1)
                ], 64))
          ]),
          _createVNode(_component_tw_button, {
            onClick: _cache[0] || (_cache[0] = ($event: any) => (payAnotherInvoice())),
            class: "btn-db-yellow mt-8 w-full"
          }, {
            default: _withCtx(() => _cache[4] || (_cache[4] = [
              _createTextVNode(" Pay Another Invoice ")
            ])),
            _: 1,
            __: [4]
          })
        ]))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _cache[5] || (_cache[5] = _createElementVNode("p", { class: "mt-2 text-sm text-gray-500 dark:text-gray-400" }, " No account needed. Enter the account number and invoice number printed on your invoice. ", -1)),
          _createElementVNode("div", _hoisted_4, [
            _createVNode(InvoiceLookupForm, {
              loading: loading.value,
              message: message.value,
              onSubmit: findInvoice
            }, null, 8, ["loading", "message"])
          ])
        ], 64)),
    _createVNode(_component_v_divider_label, {
      label: "Or",
      "class-name": "my-6"
    }),
    _createVNode(_component_router_link, {
      to: {name: 'SignIn'},
      class: "button btn-white w-full flex"
    }, {
      default: _withCtx(() => _cache[6] || (_cache[6] = [
        _createTextVNode(" Customer Login ")
      ])),
      _: 1,
      __: [6]
    }),
    (invoice.value)
      ? (_openBlock(), _createBlock(PayInvoiceDialog, {
          key: paymentToken.value,
          ref_key: "payInvoiceDialog",
          ref: payInvoiceDialog,
          invoice: invoice.value,
          "payment-token": paymentToken.value,
          onPaid: showConfirmation,
          onClosed: _cache[1] || (_cache[1] = ($event: any) => (clearInvoice())),
          onVerificationCanceled: _cache[2] || (_cache[2] = ($event: any) => (_unref(clearPendingVerification)(invoice.value))),
          onVerificationStarted: _cache[3] || (_cache[3] = ($event: any) => (markPendingVerification())),
          onVerificationCompleted: showConfirmation
        }, null, 8, ["invoice", "payment-token"]))
      : _createCommentVNode("", true),
    _createVNode(_component_cloudflare_turnstile, {
      ref_key: "turnstileRef",
      ref: turnstileRef
    }, null, 512)
  ]))
}
}

})